<template>
    <div class="two">
        <div class="two-left">
            <button-create class="mb-5" variant="green" :label="$t('habits.create_combo')"
                :subtitle="$t('habits.create_combo_hint')" icon="mdi-plus" @click="showCreate" />

            <div class="mu">
                <div class="sec-hd">
                    <span class="sec-tit">{{ $t('habits.most_used') }}</span>
                </div>
                <skeleton-loader v-if="loading" type="lib-rows" :count="1" />
                <habit-combo-card v-else-if="mostUsed" :combo="mostUsed" badge="1" @click="showEdit(mostUsed)" />
                <div v-else class="folders-empty">{{ $t('habits.no_most_used') }}</div>
            </div>

            <div class="sec-hd">
                <span class="sec-tit">{{ $t('common.your_folders') }}</span>
                <button type="button" class="sec-add" @click="showAdminFolders()">
                    <v-icon size="13">mdi-plus</v-icon>
                    {{ $t('common.folder') }}
                </button>
            </div>
            <div class="fg" v-if="currentFoldersType.length > 0">
                <folder-card v-for="folder in currentFolders" :key="folder.id" :folder="folder"
                    :is-active="folderId === folder.id" @click="onSelectFolder(folder)"
                    @click:edit="showAdminFolders(folder)" />
            </div>
            <div v-else class="folders-empty">{{ $t('common.no_folders') }}</div>
            <kimsa-pagination v-if="folderPageCount > 1" class="mt-3" center @page="folderPage = $event - 1"
                :pageCount="folderPageCount" :total-visible="isMobile ? 5 : 7" :value="folderPage + 1" />
        </div>

        <div class="rc">
            <div class="panel-srch">
                <kimsa-text-field class="srch-input" @change="setSearch" :value="search"
                    :placeholder="$t('habits.search')" prepend-inner-icon="mdi-magnify" dense />
                <button-tooltip :tooltip="$t('common.refresh')" top>
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="refresh-btn" @click="loadCombos" v-bind="attrs" v-on="on">
                            <v-icon size="16">mdi-refresh</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>
            <div class="rch">
                <span class="rct">{{ rightTitle }}</span>
            </div>

            <skeleton-loader v-if="loading" type="lib-rows" :count="perPage" />
            <div v-else class="lib-list">
                <habit-combo-card v-for="combo in pageCombos" :key="combo.id" :combo="combo" with-menu
                    @click="showEdit(combo)" @click:edit="showEdit(combo)" @click:copy="confirmCopy(combo)"
                    @click:assign="showAssign(combo)" @click:folder="showSaveFolder(combo)"
                    @click:delete="confirmDelete(combo)" />
                <div v-if="!filteredCombos.length" class="lib-empty">{{ $t('habits.no_combos') }}</div>
            </div>
            <kimsa-pagination v-if="pageCount > 1" class="mt-4" @page="page = $event - 1" :value="page + 1"
                :pageCount="pageCount" center :total-visible="isMobile ? 4 : 5" />
        </div>

        <admin-folders-dialog ref="folders" type="habitCombos" :coach-id="coachId" :folder="currentFolder"
            @hide="currentFolder = null" />
        <save-in-folder-dialog ref="saveInFolder" :current-id="selected ? selected.id : null"
            :default-folder="selected ? selected.folder : null" type="habitCombos" @saved="loadCombos"
            @cancel="selected = null" />
        <create-habit-combo-dialog ref="create" :coach-id="coachId" :company-id="companyId" @success="loadCombos" />
        <assign-habit-combo-dialog ref="assign" :coach-id="coachId" @success="loadCombos" />

        <kimsa-confirm ref="confirmDelete" :title="$t('habits.delete_combo')" :content="$t('habits.confirm_delete')"
            :persistent="false" :loading="loadingAction" @confirm="deleteCombo" @cancel="selected = null"
            :close-on-confirm="false" :alert-mode="true" />
        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="$t('habits.confirm_duplicate')"
            :persistent="false" :loading="loadingAction" @confirm="copyCombo" @cancel="selected = null"
            :close-on-confirm="false" />
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { db, Timestamp } from '@/utils/Firebase'
import { chunk } from '@/utils/Habits'
import ButtonCreate from '../../common/inputs/ButtonCreate.vue'
import FolderCard from '../../common/cards/FolderCard.vue'
import KimsaPagination from '../../common/KimsaPagination.vue'
import AdminFoldersDialog from '../AdminFoldersDialog.vue'
import SaveInFolderDialog from '../SaveInFolderDialog.vue'
import KimsaTextField from '../../common/inputs/KimsaTextField.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import KimsaConfirm from '@/components/common/KimsaConfirm'
import HabitComboCard from '@/components/habits/HabitComboCard.vue'
import CreateHabitComboDialog from '@/components/habits/CreateHabitComboDialog.vue'
import AssignHabitComboDialog from '@/components/habits/AssignHabitComboDialog.vue'
import habitsMixin from '@/mixins/habitsMixin'

export default {
    name: 'HabitsTab',
    mixins: [habitsMixin],
    components: {
        ButtonCreate, FolderCard, KimsaPagination, AdminFoldersDialog, SaveInFolderDialog, KimsaTextField,
        ButtonTooltip, SkeletonLoader, KimsaConfirm, HabitComboCard, CreateHabitComboDialog, AssignHabitComboDialog,
    },
    data() {
        return {
            search: '',
            loading: false,
            loadingAction: false,
            combos: [],
            folderId: null,
            currentFolder: null,
            folderPage: 0,
            page: 0,
            perPage: 6,
            selected: null,
        }
    },
    computed: {
        ...mapState(['folders', 'libraryCoach']),
        coachId(vm = this) {
            if (vm.isCompany) return vm.libraryCoach?.id || null
            return vm.currentUser?.uid || null
        },
        companyId(vm = this) {
            if (vm.isCompany) return vm.currentUser?.uid || null
            return vm.account?.coachSubscription?.companyId || null
        },
        currentFoldersType(vm = this) {
            return vm.folders.filter(folder => folder.type === 'habitCombos')
        },
        currentFolders(vm = this) {
            const start = vm.folderPage * 4
            return vm.currentFoldersType.slice(start, start + 4)
        },
        folderPageCount(vm = this) {
            return Math.ceil(vm.currentFoldersType.length / 4)
        },
        rightTitle(vm = this) {
            const folder = vm.folderId && vm.currentFoldersType.find(f => f.id === vm.folderId)
            return folder ? vm.folderLabel(folder) : vm.$t('habits.combos')
        },
        filteredCombos(vm = this) {
            const term = (vm.search || '').trim().toLowerCase()
            return vm.combos.filter(combo => {
                if (vm.folderId && combo.folder !== vm.folderId) return false
                if (!term) return true
                const haystack = [combo.name, ...(combo.tags || []), ...combo.habits.map(h => vm.habitName(h))]
                return haystack.some(text => (text || '').toLowerCase().includes(term))
            })
        },
        pageCount(vm = this) {
            return Math.ceil(vm.filteredCombos.length / vm.perPage)
        },
        pageCombos(vm = this) {
            const start = vm.page * vm.perPage
            return vm.filteredCombos.slice(start, start + vm.perPage)
        },
        mostUsed(vm = this) {
            const used = vm.combos.filter(combo => combo.used > 0)
            if (!used.length) return null
            return used.reduce((best, combo) => combo.used > best.used ? combo : best)
        },
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        await vm.loadCombos()
    },
    methods: {
        ...mapActions(['bindFolders', 'fetchCompanyCoaches']),
        async ownerIds() {
            const vm = this
            if (!vm.isCompany) return [vm.coachId].filter(Boolean)
            const coaches = await vm.fetchCompanyCoaches(vm.currentUser.uid)
            return [...new Set([vm.coachId, vm.currentUser.uid, ...coaches.map(c => c.objectID)].filter(Boolean))]
        },
        async loadCombos() {
            const vm = this
            await vm.withLoading(async () => {
                const ids = await vm.ownerIds()
                const snaps = await Promise.all(chunk(ids).map(group =>
                    db.collection('habitCombos').where('coachId', 'in', group).get()
                ))
                vm.combos = snaps.flatMap(snap => snap.docs.map(doc => ({ ...doc.data(), id: doc.id })))
                    .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0))
                if (vm.page >= vm.pageCount) vm.page = Math.max(0, vm.pageCount - 1)
            })
        },
        setSearch(search) {
            this.search = search || ''
        },
        onSelectFolder(folder) {
            const vm = this
            vm.folderId = vm.folderId === folder?.id ? null : (folder?.id || null)
        },
        showAdminFolders(folder) {
            const vm = this
            if (folder) vm.currentFolder = folder
            vm.$refs.folders.show()
        },
        showCreate() {
            const vm = this
            vm.$refs.create.show(null, vm.folderId)
        },
        showEdit(combo) {
            this.$refs.create.show(combo)
        },
        showAssign(combo) {
            this.$refs.assign.show(combo)
        },
        showSaveFolder(combo) {
            const vm = this
            vm.selected = combo
            vm.$nextTick(() => vm.$refs.saveInFolder.show())
        },
        confirmDelete(combo) {
            const vm = this
            vm.selected = combo
            vm.$refs.confirmDelete.show()
        },
        confirmCopy(combo) {
            const vm = this
            vm.selected = combo
            vm.$refs.confirmCopy.show()
        },
        async deleteCombo() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('habitCombos').doc(vm.selected.id).delete()
                vm.combos = vm.combos.filter(combo => combo.id !== vm.selected.id)
                vm.selected = null
                vm.$refs.confirmDelete?.hide()
                vm.toast(vm.$t('habits.combo_deleted'))
            }, 'loadingAction')
        },
        async copyCombo() {
            const vm = this
            await vm.withLoading(async () => {
                const { id, ...data } = vm.selected
                const payload = {
                    ...JSON.parse(JSON.stringify(data)),
                    name: `${data.name} ${vm.$t('common.copy_template')}`,
                    coachId: vm.coachId,
                    companyId: vm.companyId,
                    used: 0,
                    created_at: Timestamp.now(),
                }
                const ref = await db.collection('habitCombos').add(payload)
                vm.$refs.confirmCopy?.hide()
                vm.selected = null
                vm.toast(vm.$t('habits.combo_copied'))
                await vm.loadCombos()
                vm.showEdit({ ...payload, id: ref.id })
            }, 'loadingAction')
        },
    },
    watch: {
        loading(val) {
            this.$emit('loading', val)
        },
        search() {
            this.page = 0
        },
        folderId() {
            this.page = 0
        },
        coachId() {
            this.loadCombos()
        },
    },
}
</script>

<style scoped>
.two {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    align-items: start;
}

.two-left,
.rc {
    min-width: 0;
}

.mu {
    margin-bottom: 20px;
}

.sec-hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.sec-tit {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.sec-add {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    height: 26px;
    padding: 0 10px;
    border-radius: 6px;
    border: 1px solid rgba(51, 153, 153, .3);
    background: none;
    color: var(--v-accent-base);
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all .15s;
}

.sec-add:hover {
    background: rgba(51, 153, 153, .08);
}

.sec-add .v-icon {
    color: inherit !important;
}

.fg {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.folders-empty,
.lib-empty {
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 12px 0;
}

.lib-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.panel-srch {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
}

.srch-input {
    flex: 1;
}

.refresh-btn {
    width: 38px;
    height: 38px;
    min-width: 38px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    transition: all .15s;
}

.refresh-btn:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.refresh-btn .v-icon {
    color: inherit !important;
}

.rch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.rct {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

@media (max-width: 960px) {
    .two {
        grid-template-columns: 1fr;
    }
}
</style>
